// Racetracks near ski resorts (SiteConfig.features.racetracks). Data: sites/<id>/data/racetracks.ts
// Separate from ski resorts - one-off additions
import * as siteData from '@site/data/racetracks';
import * as fallbackData from '../site/fallback/data/racetracks';

// The site module's exports win; src supplies defaults (sites may ship data, helpers or both).
const site: Partial<typeof fallbackData & { getRacetrack: typeof defaultGetRacetrack }> = { ...(siteData as object) };
export const racetracks = site.racetracks ?? fallbackData.racetracks;

export interface POI {
  id: string;
  name: {
    ko: string;
    en: string;
    ja: string;
    zh?: string;
  };
  type: 'track' | 'pit' | 'grandstand' | 'hotel' | 'entrance' | 'camera' | 'city' | 'parking';
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

/** Drawn on the location map. Same fields as MapPolyline in the map component. */
export interface RacetrackRoute {
  id: string;
  name: {
    ko: string;
    en: string;
    ja: string;
    zh?: string;
  };
  color: string;
  weight?: number;
  opacity?: number;
  dashArray?: string;
  coordinates: [number, number][]; // [lat, lng][]
}

export interface Racetrack {
  id: string;
  slug: string;
  name: {
    ko: string;
    en: string;
    ja: string;
    zh?: string;
  };
  location: {
    region: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  contact: {
    homepage: string;
    maps?: {
      google?: string;
      naver?: string;
      kakao?: string;
    };
  };
  specifications: {
    mainTrackLength: number; // in km
    turns: number;
    additionalTracks?: { name: string; length: number }[];
  };
  streams: {
    id: string;
    name: {
      ko: string;
      en: string;
      ja: string;
      zh?: string;
    };
    type: 'HLS' | 'External' | 'IFrame';
    url: string;
    isLive: boolean;
    /** HTTP-only origin: browsers on https pages block it unless proxied. */
    httpOnly?: boolean;
  }[];
  description: {
    ko: string;
    en: string;
    ja: string;
    zh?: string;
  };
  pointsOfInterest?: POI[];
  /** One-hour timelapse GIF for the card/preview. */
  timelapseGifUrl?: string;
  /** Map polylines. */
  routes?: RacetrackRoute[];
  /** Editorial guide shown on the racetrack page (optional, per site). */
  guide?: RacetrackGuide;
}

type ByLang<T> = Partial<Record<'en' | 'ja' | 'ko' | 'zh', T>> & { en: T };

/** Editorial copy for a racetrack page; every field is optional except the copy. */
export interface RacetrackGuide {
  officialUrl?: string;
  hotelUrl?: string;
  phone?: string;
  address?: ByLang<string>;
  /** Ski resort slugs that pair naturally with a visit. */
  pairsWith?: string[];
  events?: { id: string; name: ByLang<string>; since?: number; rounds?: string[]; note?: ByLang<string> }[];
  /** Paths without language prefix. */
  links?: { to: string; label: ByLang<string> }[];
  copy: ByLang<{
    title: string;
    description: string;
    keywords: string;
    h1: string;
    badge: string;
    lede: string;
    factsHeading: string;
    facts: { label: string; value: string }[];
    eventsHeading: string;
    sections: { id: string; heading: string; body: string; bullets?: string[] }[];
    faqHeading: string;
    faq: { q: string; a: string }[];
    linksHeading: string;
  }>;
}

function defaultGetRacetrack(slugOrId: string): Racetrack | undefined {
  return racetracks.find(r => r.slug === slugOrId || r.id === slugOrId);
}

export const getRacetrack = site.getRacetrack ?? defaultGetRacetrack;
